import api from './api';

export interface MovMoneda {
    [key: string]: any;
}

const tryParseNumber = (value: any): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : NaN;
};

const getDateMillis = (item: any): number => {
    if (!item || typeof item !== 'object') return 0;
    // Intentar múltiples posibles nombres de campo de fecha
    const candidates = [
        item.fecha,
        item.fechaMovimiento,
        item.fechaCotizacion,
        item.fechaOperacion,
        item.fechaAlta,
        item.createdAt,
        item.updatedAt
    ];
    for (const c of candidates) {
        if (c) {
            const d = new Date(c);
            const t = d.getTime();
            if (Number.isFinite(t)) return t;
        }
    }
    return 0;
};

const getCotizacionValue = (item: any): number => {
    if (!item || typeof item !== 'object') return NaN;
    // Intentar múltiples posibles nombres de campo de cotización
    const candidates = [
        item.cotizacion,
        item.valor,
        item.valorCotizacion,
        item.tipoCambio,
        item.tc,
        item.precio,
        item.importe
    ];
    for (const c of candidates) {
        const n = tryParseNumber(c);
        if (Number.isFinite(n) && n > 0) return n;
    }
    return NaN;
};

export interface MovMonedaSearchParams {
    searchTerm?: string;
    pageNumber: number;
    pageSize: number;
    codigoMoneda?: number;
    fechaDesde?: Date | string;
    fechaHasta?: Date | string;
}

export interface MovMonedaPagedResult {
    items: MovMoneda[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

const movMonedaService = {
    getMovMonedas: async (params?: MovMonedaSearchParams): Promise<MovMonedaPagedResult | MovMoneda[]> => {
        const resp = await api.get('/api/MovMoneda', params ? { params } : {});
        const raw = resp?.data;
        
        if (Array.isArray(raw)) {
            return raw;
        }
        
        if (raw && typeof raw === 'object' && 'items' in raw) {
            return raw as MovMonedaPagedResult;
        }
        
        return [];
    },

    getMovMoneda: async (codigoMoneda: number, fechaCotizacion: string): Promise<MovMoneda> => {
        const response = await api.get<MovMoneda>(`/api/MovMoneda/${codigoMoneda}/${encodeURIComponent(fechaCotizacion)}`);
        return response.data;
    },

    getUltimaCotizacion: async (): Promise<number | null> => {
        const resp = await api.get('/api/MovMoneda');
        const raw = resp?.data;
        const items: any[] = Array.isArray(raw?.items)
            ? raw.items
            : Array.isArray(raw)
                ? raw
                : [];
        if (!Array.isArray(items) || items.length === 0) return null;

        const sorted = [...items].sort((a, b) => getDateMillis(b) - getDateMillis(a));
        for (const it of sorted) {
            const v = getCotizacionValue(it);
            if (Number.isFinite(v) && v > 0) {
                return v;
            }
        }
        return null;
    },
    
    getUltimaCotizacionPorMoneda: async (codigoMoneda: number): Promise<number | null> => {
        if (!codigoMoneda || codigoMoneda <= 0) {
            return null;
        }
        
        try {
            const fechaCotizacion = new Date().toISOString();
            const url = `/api/MovMoneda/${codigoMoneda}/${encodeURIComponent(fechaCotizacion)}`;
            const resp = await api.get(url);
            const raw = resp?.data;
            
            if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
                return raw;
            }
            
            if (raw && typeof raw === 'object') {
                const v = getCotizacionValue(raw);
                if (Number.isFinite(v) && v > 0) {
                    return v;
                }
            }
            
            return null;
        } catch (error) {
            console.error(`Error al obtener cotización para códigoMoneda ${codigoMoneda}:`, error);
            return null;
        }
    },

    createMovMoneda: async (movMoneda: MovMoneda): Promise<MovMoneda> => {
        const response = await api.post<MovMoneda>('/api/MovMoneda', movMoneda);
        return response.data;
    },

    updateMovMoneda: async (codigoMoneda: number, fechaCotizacion: string, movMoneda: MovMoneda): Promise<void> => {
        await api.put(`/api/MovMoneda/${codigoMoneda}/${encodeURIComponent(fechaCotizacion)}`, movMoneda);
    },

    deleteMovMoneda: async (codigoMoneda: number, fechaCotizacion: string): Promise<void> => {
        await api.delete(`/api/MovMoneda/${codigoMoneda}/${encodeURIComponent(fechaCotizacion)}`);
    }
};

export default movMonedaService;



